/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/core'
import styled from '@emotion/styled'
import { Fragment, FunctionComponent } from 'react'
import { COLOR_PALETTE } from '../../GlobalStyle'

type SpinnerProps = {
    circleWidth?: number
    sizeInner?: number
    sizeOuter?: number
}

const Spinner: FunctionComponent<SpinnerProps> = ({ circleWidth = 15, sizeInner = 100, sizeOuter = 120 }) => {
    const spinning = keyframes`
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }`

    const circleStyles = css`
        display: block;
        position: absolute;
        box-sizing: border-box;
        border-style: solid;
        border-radius: 50%;
        animation: ${spinning} 1.6s linear infinite;
        border-width: ${circleWidth}px;
    `

    return (
        <Fragment>
            <div
                css={css`
                    ${circleStyles};
                    border-color: ${COLOR_PALETTE.yellow};
                    border-top-color: transparent;
                    border-left-color: transparent;
                    animation-direction: reverse;
                    width: calc(${sizeInner}px - ${circleWidth}px);
                    height: calc(${sizeInner}px - ${circleWidth}px);
                `}
            />
            <div
                css={css`
                    border-color: ${COLOR_PALETTE.black};
                    border-top-color: transparent;
                    width: calc(${sizeOuter}px - ${circleWidth}px);
                    height: calc(${sizeOuter}px - ${circleWidth}px);
                    ${circleStyles};
                `}
            />
        </Fragment>
    )
}

type ContentLoaderProps = {
    height?: string
    autoHeight?: boolean
}

const ContentLoader: FunctionComponent<ContentLoaderProps> = ({ autoHeight = false, height = '100px' }) => (
    <div
        css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            min-height: ${autoHeight ? '100%' : height};
        `}
    >
        <Spinner circleWidth={7.5} sizeInner={50} sizeOuter={60} />
    </div>
)

type LoaderProps = {
    disableOpacity?: boolean
}

const Overlay = styled.div<LoaderProps>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: white;
    opacity: ${props => (props.disableOpacity ? 1 : 0.5)};
`

const OverlayContentLoader: FunctionComponent<LoaderProps> = ({ disableOpacity = false }) => {
    return (
        <Overlay disableOpacity={disableOpacity}>
            <ContentLoader autoHeight/>
        </Overlay>
    )
}

const Loader: FunctionComponent<LoaderProps> = ({ disableOpacity = false }) => {
    return (
        <div
            css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                position: fixed;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                z-index: 100;
            `}
        >
            <Overlay disableOpacity={disableOpacity} />
            <Spinner />
        </div>
    )
}

export { Loader, ContentLoader, OverlayContentLoader }
